<template>
  <AdminLayout class="pb-15">
    <v-row>
      <v-col cols="12">
        <v-alert
          class="custom-alert"
          v-if="!bookableCountCheck"
          type="info"
          elevation="2"
        >
          Sie haben die maximale Anzahl an öffentlichen Buchungsobjekten
          erreicht. Erweitern Sie Ihr Kontingent, oder löschen Sie nicht mehr
          benötigte Buchungsobjekte.
        </v-alert>
      </v-col>
    </v-row>
    <BookableSearchAndFilter
      type="Räume"
      @update:search="searchQuery = $event"
      @update:filters="filters = $event"
    />
    <v-row class="align-stretch pb-15" :gutter="16">
      <v-col
        v-for="room in filteredRooms"
        :key="room.id"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        class="d-flex"
      >
        <v-skeleton-loader type="card" class="d-flex flex-column fill-height">
          <bookable-card
            :item="room"
            edit-route="room-edit"
            :from-route="$router.currentRoute.value.name"
            @delete="removeBookable(room.id)"
            @duplicate="duplicateBookable(room.id)"
          />
        </v-skeleton-loader>
      </v-col>
    </v-row>

    <v-fab-transition>
      <v-btn
        color="primary"
        fixed
        size="large"
        rounded
        class="position-fixed bottom-0 right-0 mb-4 mr-5"
        :to="{
          name: 'room-edit',
          query: { fromRoute: $router.currentRoute.value.name },
        }"
        :disabled="createDisabled"
      >
        <v-icon>mdi-plus</v-icon> Raum Erstellen
      </v-btn>
    </v-fab-transition>
  </AdminLayout>
</template>

<script>
import AdminLayout from "@/layouts/Admin.vue";
import BookableCard from "@/components/BookableCard.vue";
import BookableSearchAndFilter from "@/components/Filters/BookableSearchAndFilter.vue";
import { mapActions, mapGetters } from "vuex";
import ApiBookablesService from "@/services/api/ApiBookablesService";
import ToastService from "@/services/ToastService";
import BookablePermissionService from "@/services/permissions/BookablePermissionService";

export default {
  components: {
    AdminLayout,
    BookableCard,
    BookableSearchAndFilter,
  },
  data() {
    return {
      api: {
        rooms: [],
        tags: [],
      },
      filters: {},
      searchQuery: "",
      bookableCountCheck: true,
    };
  },
  computed: {
    ...mapGetters({
      loading: "loading/isLoading",
      tenantId: "tenants/currentTenantId",
    }),
    createDisabled() {
      return !this.BookablePermissionService.allowCreate();
    },
    BookablePermissionService() {
      return BookablePermissionService;
    },
    filteredRooms() {
      const rooms = this.api.rooms || [];
      const f = this.filters;

      const hasFilter =
        this.searchQuery ||
        (f.selectedTags && f.selectedTags.length > 0) ||
        f.isBookable !== null ||
        f.isPublic !== null ||
        f.allowRecurringBooking !== null ||
        f.autoCommitBooking !== null;

      if (!hasFilter) return rooms;

      return rooms.filter((room) => {
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          const matchesSearch =
            (room.title && room.title.toLowerCase().includes(query)) ||
            (room.description &&
              room.description.toLowerCase().includes(query)) ||
            (room.location && room.location.toLowerCase().includes(query));
          if (!matchesSearch) return false;
        }
        if (f.selectedTags && f.selectedTags.length > 0) {
          const hasAllTags = f.selectedTags.every((tag) =>
            room.tags?.includes(tag)
          );
          if (!hasAllTags) return false;
        }
        if (f.isBookable !== null && room.isBookable !== f.isBookable)
          return false;
        if (f.isPublic !== null && room.isPublic !== f.isPublic) return false;
        if (
          f.allowRecurringBooking !== null &&
          room.allowRecurringBooking !== f.allowRecurringBooking
        )
          return false;
        if (
          f.autoCommitBooking !== null &&
          room.autoCommitBooking !== f.autoCommitBooking
        )
          return false;

        return true;
      });
    },
  },
  watch: {
    tenantId() {
      this.fetchRooms();
    },
  },
  methods: {
    ...mapActions({
      startLoading: "loading/start",
      stopLoading: "loading/stop",
      addToast: "toasts/add",
    }),
    remove(item) {
      this.filters.splice(this.filters.indexOf(item), 1);
    },
    async removeBookable(bookableId) {
      await this.startLoading("fetch-delete-bookable");
      ApiBookablesService.deleteBookable(bookableId)
        .then(() => {
          this.fetchRooms();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.delete.success", "success")
          );
          this.stopLoading("fetch-delete-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast(
              "bookable.duplicate.errors.something-wrong",
              "error"
            )
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    async duplicateBookable(bookableId) {
      await this.startLoading("fetch-duplicate-bookable");

      ApiBookablesService.duplicateBookable(bookableId)
        .then(() => {
          this.fetchRooms();
        })
        .finally(() => {
          this.addToast(
            ToastService.createToast("bookable.duplicate.success", "success")
          );
          this.stopLoading("fetch-duplicate-bookable");
        })
        .catch((error) => {
          this.addToast(
            ToastService.createToast("errors.something-wrong", "error")
          );
          console.log(error);
        });
      await this.getBookableCount();
    },
    fetchRooms() {
      this.startLoading("fetch-rooms");
      ApiBookablesService.getBookables()
        .then((response) => {
          this.api.rooms = response.data.filter(
            (resource) => resource.type === "room"
          );
        })
        .finally(() => {
          this.stopLoading("fetch-rooms");
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async getBookableCount() {
      this.bookableCountCheck =
        await ApiBookablesService.publicBookableCountCheck();
    },
  },
  async mounted() {
    await this.getBookableCount();
  },
  created() {
    this.fetchRooms();
  },
};
</script>
<style scoped>
.filter-field {
  border-radius: 15px;
}
.custom-alert {
  border-radius: 15px !important;
}
</style>
